import { IVacationBidSlot } from "@vmosolutions/oc-interfaces";

import { buildSlotId, compareVacationDates, isoToVacationDate, vacationDateToIso } from "../helpers";
import {
  ICsvFile,
  ICsvIssue,
  ICsvParseResult,
  missingHeaders,
  parseCsvBoolean,
  parseCsvWholeNumber,
  toCsvText,
} from "src/utils/csvFile";

const START_DATE = "startdate";
const END_DATE = "enddate";
const CAPACITY = "capacity";
const INCLUDE = "include";

/** The columns a slot file must carry. `include` is optional and defaults to included. */
export const SLOT_CSV_REQUIRED_HEADERS = [START_DATE, END_DATE, CAPACITY];

/**
 * Reads a slot file into blocks.
 *
 * A line that cannot be read in full is reported and skipped rather than imported with a guessed
 * value, so a malformed file can never quietly change a capacity. Slot ids are derived from the
 * start date exactly as the backend derives them, so a file never carries one.
 */
export const slotsFromCsv = (file: ICsvFile): ICsvParseResult<IVacationBidSlot> => {
  if (file.fatal) return { rows: [], issues: [], fatal: file.fatal };

  const headerProblem = missingHeaders(file, SLOT_CSV_REQUIRED_HEADERS);
  if (headerProblem) return { rows: [], issues: [], fatal: headerProblem };

  const issues: ICsvIssue[] = [];
  const rows: IVacationBidSlot[] = [];
  // Slot ids are unique within a package, so a repeated start date is a mistake in the file.
  const lineBySlotId = new Map<string, number>();

  for (const row of file.rows) {
    const { line, values } = row;

    const startDate = isoToVacationDate(values[START_DATE]);
    if (!startDate) {
      issues.push({ line, message: `Start date "${values[START_DATE] ?? ""}" is not a YYYY-MM-DD date.` });
      continue;
    }

    const endDate = isoToVacationDate(values[END_DATE]);
    if (!endDate) {
      issues.push({ line, message: `End date "${values[END_DATE] ?? ""}" is not a YYYY-MM-DD date.` });
      continue;
    }

    if (compareVacationDates(endDate, startDate) < 0) {
      issues.push({ line, message: "End date falls before the start date." });
      continue;
    }

    const capacity = parseCsvWholeNumber(values[CAPACITY]);
    if (capacity === undefined) {
      issues.push({ line, message: `Capacity "${values[CAPACITY] ?? ""}" is not a whole number of zero or more.` });
      continue;
    }

    let include = true;
    if (values[INCLUDE]) {
      const parsed = parseCsvBoolean(values[INCLUDE]);
      if (parsed === undefined) {
        issues.push({ line, message: `Include "${values[INCLUDE]}" is not true or false.` });
        continue;
      }
      include = parsed;
    }

    const slotId = buildSlotId(startDate);
    const firstLine = lineBySlotId.get(slotId);
    if (firstLine !== undefined) {
      issues.push({ line, message: `Block ${slotId} is already defined on line ${firstLine}.` });
      continue;
    }
    lineBySlotId.set(slotId, line);

    rows.push({ slotId, startDate, endDate, capacity, include });
  }

  rows.sort((a, b) => compareVacationDates(a.startDate, b.startDate));
  return { rows, issues };
};

/**
 * The columns an exported block file carries, in the spelling it writes them.
 *
 * `normalizeHeader` maps each of these to the key {@link slotsFromCsv} looks for, so a file written
 * here is one it reads back. A test holds the two together, since a rename on either side would
 * otherwise break the round trip silently.
 *
 * `slotId` is deliberately absent: it is derived from `startDate`, and writing it would invite
 * someone to edit it.
 */
export const SLOT_CSV_HEADERS = ["startDate", "endDate", "capacity", "include"];

/** Writes blocks in the format {@link slotsFromCsv} reads. */
export const slotsToCsv = (slots: IVacationBidSlot[]): string =>
  toCsvText(
    SLOT_CSV_HEADERS,
    slots.map((slot) => [
      vacationDateToIso(slot.startDate),
      vacationDateToIso(slot.endDate),
      String(slot.capacity),
      String(slot.include),
    ])
  );
